/**
 * AssignFormsDialog Component
 * Modal para asignar/desasignar formularios a un negocio
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Alert,
  CircularProgress,
  Divider,
  Chip,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import formService from '../../../services/formService';
import businessFormsService from '../../../services/businessForms';

interface AssignFormsDialogProps {
  open: boolean;
  onClose: () => void;
  businessId: number;
  businessName: string;
  currentFormIds: number[];
  onSuccess: () => void;
}

interface Form {
  id: number;
  name: string;
  description: string;
  active: boolean;
}

const AssignFormsDialog: React.FC<AssignFormsDialogProps> = ({
  open,
  onClose,
  businessId,
  businessName,
  currentFormIds,
  onSuccess,
}) => {
  const [allForms, setAllForms] = useState<Form[]>([]);
  const [selectedFormIds, setSelectedFormIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadAllForms();
      setSelectedFormIds([...currentFormIds]);
    }
  }, [open, currentFormIds]);

  const loadAllForms = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await formService.list({ active: true, limit: 100 });
      const formsData = response.data.data || response.data;
      setAllForms(Array.isArray(formsData) ? formsData : []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al cargar formularios');
      console.error('Error cargando formularios:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleForm = (formId: number) => {
    setSelectedFormIds(prev =>
      prev.includes(formId)
        ? prev.filter(id => id !== formId)
        : [...prev, formId]
    );
  };

  const handleSelectAll = () => {
    if (selectedFormIds.length === allForms.length) {
      setSelectedFormIds([]);
    } else {
      setSelectedFormIds(allForms.map(f => f.id));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Usar PUT para reemplazar todas las asignaciones
      await businessFormsService.updateForms(businessId, {
        form_ids: selectedFormIds,
      });

      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al guardar asignaciones');
      console.error('Error guardando asignaciones:', err);
    } finally {
      setSaving(false);
    }
  };

  const changesCount = () => {
    const added = selectedFormIds.filter(id => !currentFormIds.includes(id)).length;
    const removed = currentFormIds.filter(id => !selectedFormIds.includes(id)).length;
    return { added, removed, total: added + removed };
  };

  const changes = changesCount();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box>
          <Typography variant="h6">
            📋 Gestionar Formularios
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {businessName}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Header con controles */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Selecciona los formularios disponibles para este negocio
              </Typography>
              <Button size="small" onClick={handleSelectAll}>
                {selectedFormIds.length === allForms.length ? 'Deseleccionar todo' : 'Seleccionar todo'}
              </Button>
            </Box>

            {/* Lista de formularios */}
            {allForms.length === 0 ? (
              <Alert severity="warning">
                No hay formularios activos en el sistema
              </Alert>
            ) : (
              <FormGroup>
                {allForms.map((form) => (
                  <FormControlLabel
                    key={form.id}
                    control={
                      <Checkbox
                        checked={selectedFormIds.includes(form.id)}
                        onChange={() => handleToggleForm(form.id)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{form.name}</span>
                          {currentFormIds.includes(form.id) && (
                            <Chip
                              label="Asignado actualmente"
                              size="small"
                              color="primary"
                            />
                          )}
                        </Typography>
                        {form.description && (
                          <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                            {form.description}
                          </Typography>
                        )}
                      </Box>
                    }
                    sx={{
                      py: 1,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': {
                        borderBottom: 'none',
                      },
                    }}
                  />
                ))}
              </FormGroup>
            )}

            {/* Resumen de cambios */}
            {changes.total > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="body2" fontWeight={500} gutterBottom>
                    Cambios pendientes:
                  </Typography>
                  {changes.added > 0 && (
                    <Typography variant="body2" color="success.main">
                      • {changes.added} formulario{changes.added !== 1 ? 's' : ''} a agregar
                    </Typography>
                  )}
                  {changes.removed > 0 && (
                    <Typography variant="body2" color="error.main">
                      • {changes.removed} formulario{changes.removed !== 1 ? 's' : ''} a desasignar
                    </Typography>
                  )}
                </Box>
              </>
            )}

            {/* Contador */}
            <Box sx={{ mt: 2, p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>{selectedFormIds.length}</strong> de <strong>{allForms.length}</strong> formularios seleccionados
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={loading || saving || allForms.length === 0}
        >
          {saving ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignFormsDialog;
